import { Image, Loader2, Upload, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSaveImageEdit } from '../api/templates';
import { Button } from '@/shared/ui/button.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form.tsx"
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Textarea } from '@/shared/ui/textarea.tsx';
import { Input } from '@/shared/ui/input';

type Props = {
  event_id: number,
  image_id: number,
  template_name: string,
  onShowUploadChange: (val: boolean) => void
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        resolve(result);
      } else {
        reject(new Error("Failed to convert file to base64"));
      }
    };

    reader.onerror = (error) => reject(error);
  });
};

const formSchema = z.object({
  file: z
    .instanceof(File, { message: "An image file is required" })
    .refine((file) => file.size <= MAX_FILE_SIZE, {
      message: "File size must be less than 10MB",
    }),
  catalogItem: z.string({ required_error: "Please select a catalog item" }).min(1, "Please select a catalog item"),
  prompt: z.string().min(1, { message: "Image Description is required" })
})

const UploadImageInput = ({ template_name, image_id, onShowUploadChange }: Props) => {

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      catalogItem: template_name,
    },
  })

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const { saveImageEdit, status } = useSaveImageEdit()

  const { watch, setValue, formState, resetField } = form
  const watchedFile = watch("file")

  // Update preview when file changes
  useEffect(() => {
    if (watchedFile instanceof File) {
      const url = URL.createObjectURL(watchedFile)
      setPreviewUrl(url)

      // Cleanup function to revoke object URL
      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [watchedFile])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null
    if (selectedFile) {
      setValue("file", selectedFile, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    }
  }

  const handleRemoveFile = () => {
    resetField("file")
    setPreviewUrl(null)
  }

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const uri = await fileToBase64(data.file)
    saveImageEdit({ image_file: uri, image_id: image_id })
  }

  return (
    <div className="w-full p-6 border rounded-xl h-full">
      <div className="flex flex-col md:flex-row gap-8 items-center h-full">
        {/* Left Section - Upload Controls */}
        <div className="flex-1 space-y-4 w-full">
          <div className='w-full flex flex-col sm:flex-row gap-3 items-center sm:justify-between'>
            <h2 className="text-xl font-semibold">Upload Image</h2>
            <Button
              onClick={() => onShowUploadChange(false)}
              variant="outline" className='text-sm flex gap-2 items-center'>
              <Image />
              Select from gallery
            </Button>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* File Upload Field */}
              <FormField
                control={form.control}
                name="file"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <div
                          className={`relative flex flex-col items-center justify-center rounded-lg border ${formState.errors.file ? "border-red-500 bg-red-50" : "border-dashed border-gray-700 bg-gray-200"} p-6 text-center cursor-pointer hover:bg-gray-300 transition-colors h-40`}
                        >
                          <Upload
                            className={`h-10 w-10 mb-2 ${formState.errors.file ? "text-red-500" : "text-gray-800"}`}
                          />
                          <div className="text-lg font-medium">Upload an image</div>
                          <p className="text-xs text-gray-700 mt-1">PNG, JPG or WEBP up to 10MB</p>
                          <input
                            type="file"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={handleFileChange}
                          />
                        </div>
                        <FormMessage />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* File Details (when file is selected) */}
              {watchedFile && (
                <div className="flex items-center justify-between p-3 border rounded-md bg-gray-50">
                  <div>
                    <p className="text-sm font-medium">
                      {watchedFile.name.length > 20 ? `${watchedFile.name.slice(0, 20)}...` : watchedFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">{(watchedFile.size / 1024).toFixed(2)} KB</p>
                  </div>
                  <Button variant="outline" type="button" size="sm" onClick={handleRemoveFile}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Catalog Item Select */}
              <FormField
                control={form.control}
                name="catalogItem"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input {...field} value={template_name} disabled />
                    </FormControl>
                  </FormItem>
                )}
              />
              {/* Image description */}
              <FormField
                control={form.control}
                name="prompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Image Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="describe the image..." {...field} className='resize-none' />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormMessage />
              {/* Upload Button */}
              <Button
                type="submit"
                disabled={status === "pending" || !form.formState.isValid}
                variant="outline"
                className="w-32 rounded-full border border-purple-500 font-poppins tracking-wide"
              >
                {status === "pending" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Uploading
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </form>
          </Form>
        </div>

        {/* Vertical Divider */}
        <div className="hidden md:block w-px bg-gray-200 self-stretch"></div>

        {/* Right Section - Image Preview */}
        <div className="flex-1 w-full flex items-center justify-center border rounded-lg p-4 min-h-[300px]">
          {previewUrl ? (
            <div className="relative w-full h-full min-h-[250px]">
              <img src={previewUrl || "/placeholder.svg"} alt="Preview" className="object-contain" />
            </div>
          ) : (
            <div className="text-gray-400 text-lg">Image Preview</div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UploadImageInput;
